/**
 * What counts as a match in the trace tree.
 *
 * Kept out of TracePanel so the rule can be tested directly — it decides what
 * the user sees, and it has to stay in step with the backend.
 *
 * `terms` are the word stems the BACKEND matched on, returned by
 * GET /agent-runs/:id/search. Using them is what keeps this tree agreeing with
 * the results dropdown sitting inches above it.
 *
 * This used to be `haystack.includes(wholeQuery)` and nothing else — the exact
 * literal-substring rule the backend search itself used to have. Once the
 * backend learned to stem, the two diverged visibly: on production, searching
 * "retention of users who came back" listed 13 results in the dropdown while
 * the tree beside it said "No spans match". Both did what they were told; only
 * one had been taught how people actually type.
 *
 * The rule mirrors the backend's: the literal phrase always matches, otherwise
 * half the stems must land (the server requires hits >= ceil(stems/2)).
 * Matching on any single stem would drag half the trace back for a five-word
 * query.
 *
 * Stems arrive with the debounced search response, so `terms` is empty for the
 * first few hundred milliseconds of typing. The literal check covers that
 * window — and it is the case literal matching is best at, someone typing an
 * exact word — then the tree converges on the dropdown's answer once the
 * response lands.
 */

/** Joins the searched fields. A query can never contain it, so the end of one
 *  field and the start of the next cannot combine into a match in neither. */
export const FIELD_SEP = ' \u0001 ';

/** Does a span match? Searches name, kind, model, provider, tool, status, and
 *  the input / output / attributes payloads. */
export function spanMatches(node, q, terms) {
  if (!q && !(terms && terms.length)) return true;
  const parts = [
    node.name, node.kind, node.model, node.provider, node.tool_name, node.status,
    typeof node.input === 'string' ? node.input : node.input ? JSON.stringify(node.input) : '',
    typeof node.output === 'string' ? node.output : node.output ? JSON.stringify(node.output) : '',
    node.attributes ? JSON.stringify(node.attributes) : '',
  ];
  const hay = parts.join(FIELD_SEP).toLowerCase();
  if (q && hay.includes(q)) return true;
  if (!terms || !terms.length) return false;
  const need = Math.max(1, Math.ceil(terms.length / 2));
  let hits = 0;
  for (const t of terms) {
    if (t && hay.includes(t)) {
      hits += 1;
      if (hits >= need) return true;
    }
  }
  return false;
}

/**
 * Prune the tree to spans on a path to a match: a matching node keeps its full
 * subtree (for context); a non-matching node is kept only if a descendant
 * matches, so a deep hit stays reachable.
 */
export function filterTree(nodes, q, terms, scoredIds = null) {
  if (!q && !(terms && terms.length)) return nodes;
  const out = [];
  nodes.forEach((n) => {
    // A span an eval's verdict matched is a match too, though its text is not.
    if (spanMatches(n, q, terms) || scoredIds?.has(String(n.span_id))) {
      out.push(n);
    } else {
      const kids = filterTree(n.children || [], q, terms, scoredIds);
      if (kids.length) out.push({ ...n, children: kids });
    }
  });
  return out;
}
